import { computed, toValue, type MaybeRefOrGetter } from "vue";

import { useApiQuery, type QueryKey } from "@ja/portal-query";

/** API response format for unique check */
interface UniqueCheckResponse {
    isUnique: boolean;
    error?: string;
}

/** Parameters for useUniqueCheck composable */
export interface UseUniqueCheckParams {
    /** Model name for the check-unique endpoint */
    model: MaybeRefOrGetter<string | undefined>;
    /** Field name to check */
    field: MaybeRefOrGetter<string>;
    /** Value to check for uniqueness */
    value: MaybeRefOrGetter<string | null | undefined>;
    /** Record ID to exclude from check (for updates) */
    ignoreId?: MaybeRefOrGetter<string | number | null | undefined>;
    /** Column name for ignore (defaults to 'id') */
    ignoreColumn?: MaybeRefOrGetter<string>;
    /** Whether the query is enabled */
    enabled?: MaybeRefOrGetter<boolean>;
}

/**
 * Builds the query key for unique check.
 * Using a consistent key structure enables proper caching and deduplication.
 */
function buildQueryKey(
    model: string,
    field: string,
    value: string,
    ignoreId: string | number | null | undefined
): QueryKey {
    return ["crud", "check-unique", model, field, value, ignoreId ?? "new"];
}

/**
 * Composable for checking field value uniqueness with TanStack Query.
 *
 * Provides automatic caching, deduplication, and debounce-friendly behavior.
 * Query key structure: ['crud', 'check-unique', model, field, value, ignoreId]
 *
 * @example
 * ```typescript
 * // Use with refDebounced for optimal UX
 * const debouncedValue = refDebounced(fieldValue, 400)
 *
 * const { isUnique, isChecking, error } = useUniqueCheck({
 *   model: 'user',
 *   field: 'email',
 *   value: debouncedValue,
 *   ignoreId: props.itemId, // For edit mode
 * })
 * ```
 */
export function useUniqueCheck(params: UseUniqueCheckParams) {
    const {
        model,
        field,
        value,
        ignoreId,
        ignoreColumn = "id",
        enabled = true
    } = params;

    const queryKey = computed(() => {
        const m = toValue(model);
        const f = toValue(field);
        const v = toValue(value);
        const i = toValue(ignoreId);

        if (!m || !f || !v) {
            return ["crud", "check-unique", "disabled"];
        }
        return buildQueryKey(m, f, v, i);
    });

    const isEnabled = computed(() => {
        const m = toValue(model);
        const v = toValue(value);
        // Only enable when we have model and non-empty value
        return !!m && v != null && v !== "" && toValue(enabled);
    });

    const result = useApiQuery<UniqueCheckResponse>(
        queryKey,
        () => {
            const m = toValue(model);
            const f = toValue(field);
            const v = toValue(value);
            const i = toValue(ignoreId);
            const ic = toValue(ignoreColumn);

            let url = `/crud/${m}/check-unique?field=${f}&value=${encodeURIComponent(v)}`;
            if (i) {
                url += `&ignore_id=${i}&ignore_column=${ic}`;
            }
            return url;
        },
        {},
        {
            enabled: isEnabled,
            // Short stale time since uniqueness can change quickly
            staleTime: 10 * 1000, // 10 seconds
            // Cache for 2 minutes
            gcTime: 2 * 60 * 1000,
            // Don't retry on failure - just assume unique to avoid blocking
            retry: false,
            // Return previous data while fetching new results
            placeholderData: (previousData: any) => previousData
        }
    );

    return {
        /** Whether the value is unique */
        isUnique: computed(() => result.data.value?.isUnique ?? true),
        /** Whether the check is currently loading */
        isChecking: result.isLoading,
        /** Whether the check is fetching (includes background refetches) */
        isFetching: result.isFetching,
        /** Error from the query */
        error: computed(() => result.data.value?.error ?? null),
        /** Refetch the uniqueness check */
        refetch: result.refetch
    };
}
